import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Shopify from '../Shopify';
import Woocommerce from '../Woocommerce';
import Prestashop from '../Prestashop';
import Meli from '../Meli';
import Bootic from '../Bootic';
import Vtex from '../Vtex';

import {
  createEcommerceIntegration,
  initMeliAuth,
  initShopifyAuth,
  updateEcommerceIntegration,
} from '../../../../../features/ecommerces/ecommercesThunks';
import { MELI_SLUG, SHOPIFY_SLUG } from '../../../../../features/ecommerces/ecommercesPlatforms';
import { rememberOauthStart } from '../../../../../features/ecommerces/oauthReturn';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';

/**
 * The OAuth forms were written against the legacy hook, which answered with
 * `{ success, redirectUri, error }`, and they redirect by themselves. This keeps
 * that contract, and writes down the record the init created before the form
 * sends the seller away, so the return lands on this integration.
 */
const asRedirect = (action, platform) =>
  action
    .unwrap()
    .then(({ url, integrationId }) => {
      if (integrationId) rememberOauthStart({ id: integrationId, platform });
      return { success: true, redirectUri: url, error: null };
    })
    .catch((error) => ({ success: false, redirectUri: null, error }));

/**
 * The legacy `switch` of `MainEcommerce`, on the integration's name: one form
 * per platform, unchanged. Only Shopify and Mercado Libre get `onConnect`.
 *
 * @param {object} props
 * @param {object} props.ecommerce - Stored integration or template.
 * @param {boolean} props.isEdit
 * @param {Array<object>} props.warehouses - Already narrowed by `getSelectableWarehouses`.
 * @param {(formData: object) => void} props.onSave - Creates in "new" mode and
 *   updates otherwise; each form's submit spins while that request is out.
 */
const EcommerceForm = ({ ecommerce, isEdit, warehouses, onSave }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.ecommerces.loading);
  // `loading` is up for any request of the slice, the page's own fetch
  // included, so the spinner on the submit reads this form's save alone.
  const saving = useSelector(
    isEdit
      ? selectIsRunning(updateEcommerceIntegration, ecommerce.id)
      : selectIsRunning(createEcommerceIntegration)
  );

  const connectShopify = (shopUrl, credentials, warehouse) =>
    asRedirect(dispatch(initShopifyAuth({ shopUrl, credentials, warehouse })), SHOPIFY_SLUG);

  const connectMeli = (integrationData) =>
    asRedirect(dispatch(initMeliAuth(integrationData)), MELI_SLUG);

  const props = { warehouses, loading, saving, ecommerce, onSave, isEdit };

  switch (ecommerce.name.toLowerCase()) {
    case "shopify":
      return <Shopify {...props} onConnect={connectShopify} />;
    case "woocommerce":
      return <Woocommerce {...props} />;
    case "prestashop":
      return <Prestashop {...props} />;
    case "mercado libre":
      return <Meli {...props} onConnect={connectMeli} />;
    case "bootic":
      return <Bootic {...props} />;
    case "vtex":
      return <Vtex {...props} />;
    default:
      return (
        <div className="bg-orange-50 dark:bg-orange-900/20 text-warning p-3 rounded-xl border border-orange-200 dark:border-orange-900 text-sm">
          Configuración no disponible para {ecommerce.name}
        </div>
      );
  }
};

export default memo(EcommerceForm);
